// @mui
import Card from '@mui/material/Card';
import { Box, Paper, TextField, Grid, Stack, Button, Typography, CircularProgress } from '@mui/material';
import CardContent from '@mui/material/CardContent';
// hooks
import { useEffect, useState, useRef } from 'react';
// components
import Iconify from 'src/components/iconify';
import { getRequest, postRequestForWebsite } from 'src/services';
import { endpoints } from 'src/utils/axios';
import { handleErrorMessages } from 'src/services/fetching';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { useSelector } from 'react-redux';
import useConstructionSection from 'src/hooks/use-construction-page';

// ----------------------------------------------------------------------

export default function Construction() {


  const constructionPageData = useSelector((state) => state.website.constructionPage);
  const { fetchOnMount } = useConstructionSection();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const lastFeatureSectionRef = useRef(null);

  const [formData, setFormData] = useState({
    // Main Header
    mainTitle: "",
    mainTitleColor: "#000000",
    subtitle: "",
    subtitleColor: "#666666",

    // Feature Sections
    featureSections: [
      {
        // id: 'user-friendly',
        title: "",
        titleColor: "#4CAF50",
        items: [
          {
            // id: 'seamless-experience',
            text: "",
            textColor: "#333333"
          }
        ]
      },

    ],

    // Why Choose Section
    whyChooseTitle: "",
    whyChooseTitleColor: "#000000",
    whyChooseDescription: "",
    whyChooseDescriptionColor: "#666666"
  });


  const updateFormData = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (typeof current[key] !== 'object' || Array.isArray(current[key])) {
          current[key] = {};
        }
        current = current[key];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  const updateFeatureSection = (sectionIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      featureSections: prev.featureSections.map((section, i) =>
        i === sectionIndex ? { ...section, [field]: value } : section
      )
    }));
  };

  const updateFeatureItem = (sectionIndex, itemIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      featureSections: prev.featureSections.map((section, i) =>
        i === sectionIndex ? {
          ...section,
          items: section.items.map((item, j) =>
            j === itemIndex ? { ...item, [field]: value } : item
          )
        } : section
      )
    }));
  };

  const addFeatureSection = () => {
    const newSection = {
      id: `section-${Date.now()}`,
      title: "",
      titleColor: "#4CAF50",
      items: [
        {
          id: `item-${Date.now()}`,
          text: "",
          textColor: "#333333"
        }
      ]
    };

    setFormData(prev => ({
      ...prev,
      featureSections: [...prev.featureSections, newSection]
    }));

    // Scroll to the newly added section after a short delay to ensure DOM update
    setTimeout(() => {
      if (lastFeatureSectionRef.current) {
        lastFeatureSectionRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
    setTimeout(() => {
      const element = document.getElementById(`section-${Date.now()}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const removeFeatureSection = (sectionIndex) => {
    setFormData(prev => ({
      ...prev,
      featureSections: prev.featureSections.filter((_, i) => i !== sectionIndex)
    }));
  };

  const addFeatureItem = (sectionIndex) => {
    const newItem = {
      id: `item-${Date.now()}`,
      text: "",
      textColor: "#333333"
    };

    setFormData(prev => ({
      ...prev,
      featureSections: prev.featureSections.map((section, i) =>
        i === sectionIndex ? {
          ...section,
          items: [...section.items, newItem]
        } : section
      )
    }));
  };

  const removeFeatureItem = (sectionIndex, itemIndex) => {
    setFormData(prev => ({
      ...prev,
      featureSections: prev.featureSections.map((section, i) =>
        i === sectionIndex ? {
          ...section,
          items: section.items.filter((_, j) => j !== itemIndex)
        } : section
      )
    }));
  };


  useEffect(() => {
      fetchOnMount();
    }, [fetchOnMount]);

    useEffect(() => {
      if (constructionPageData?.construction_page) {
        // Create a deep copy to avoid mutating the read-only Redux store data
        const deepCopy = JSON.parse(JSON.stringify(constructionPageData.construction_page));
        setFormData(prev => ({
          ...prev,
          ...deepCopy
        }));
      }
    }, [constructionPageData])

  const handleSave = async () => {
    setLoading(true);

    try {
      // Create a FormData object for file uploads
      const formDataToSend = new FormData();

      // Append all form data
      Object.entries(formData).forEach(([key, value]) => {
        // Skip heroImage as we'll handle it separately
        if (key !== 'heroImage') {
          // Handle nested objects like primaryButton and secondaryButton
          if (typeof value === 'object' && value !== null && !(value instanceof File)) {
            formDataToSend.append(key, JSON.stringify(value));
          } else {
            formDataToSend.append(key, value);
          }
        }
      });

      // Append the hero image if it exists
      if (formData.heroImage) {
        formDataToSend.append('heroImage', formData.heroImage);
      }

      // Make sure heroImageUrl is included in the submission
      if (formData.heroImageUrl) {
        formDataToSend.append('heroImageUrl', formData.heroImageUrl);
      }

      const response = await postRequestForWebsite(
        endpoints.admin_dashboard.website.construction_page.construction_section,
        // formDataToSend,
        // dataForHeroSection,
        {
          attributes: {
            ...JSON.parse(JSON.stringify(constructionPageData)),
            construction_page: formData
          }
        },
        true
      );

      if (response.metadata?.success) {
        enqueueSnackbar({
          variant: 'success',
          message: response.metadata.message,
        });
      } else {
        handleErrorMessages(response.message || 'An error occurred.');
      }
    } catch (err) {
      console.error('Error:', err);
      handleErrorMessages(err.message || 'Submission failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h4" paragraph>
          Construction
        </Typography>

        {/* Main Header Section */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Main Header</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Main Title"
                value={formData.mainTitle}
                onChange={(e) => updateFormData('mainTitle', e.target.value)}
                placeholder="Enter main title"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Title Color"
                type="color"
                value={formData.mainTitleColor}
                onChange={(e) => updateFormData('mainTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Subtitle"
                value={formData.subtitle}
                onChange={(e) => updateFormData('subtitle', e.target.value)}
                placeholder="Enter subtitle"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Subtitle Color"
                type="color"
                value={formData.subtitleColor}
                onChange={(e) => updateFormData('subtitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Feature Sections */}
        <Box sx={{ mb: 5, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Stack direction="row" alignItems="center">
              <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
              <Typography variant="h6">Feature Sections</Typography>
            </Stack>
            <Button
              variant="contained"
              color="success"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={addFeatureSection}
            >
              Add Feature Section
            </Button>
          </Stack>

          {formData.featureSections.map((section, sectionIndex, array) => (
            <Paper
              key={section.id}
              variant="outlined"
              sx={{ p: 3, mb: 3, bgcolor: 'background.neutral' }}
              ref={sectionIndex === array.length - 1 ? lastFeatureSectionRef : null}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" color="primary">
                  Feature Section {sectionIndex + 1}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    onClick={() => removeFeatureSection(sectionIndex)}
                    startIcon={<Iconify icon="mingcute:close-line" />}
                  >
                    Remove
                  </Button>
                </Stack>
              </Stack>

              {/* Section Header */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Section ID"
                    value={section.id}
                    onChange={(e) => updateFeatureSection(sectionIndex, 'id', e.target.value)}
                  />
                </Grid> */}
                <Grid item xs={12} md={10}>
                  <TextField
                    fullWidth
                    label="Section Title"
                    value={section.title}
                    onChange={(e) => updateFeatureSection(sectionIndex, 'title', e.target.value)}
                    placeholder="Enter section title"
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="Title Color"
                    type="color"
                    value={section.titleColor}
                    onChange={(e) => updateFeatureSection(sectionIndex, 'titleColor', e.target.value)}
                    sx={{ '& input': { height: 20 } }}
                  />
                </Grid>
              </Grid>

              {/* Section Items */}
              <Box sx={{ mb: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Feature Items ({section.items.length})
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<Iconify icon="mingcute:add-line" />}
                    onClick={() => addFeatureItem(sectionIndex)}
                  >
                    Add Item
                  </Button>
                </Stack>

                {section.items.map((item, itemIndex) => (
                  <Paper
                    key={item.id}
                    variant="outlined"
                    sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="caption" color="primary">
                        Item {itemIndex + 1}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => removeFeatureItem(sectionIndex, itemIndex)}
                          sx={{ minWidth: 25, p: 0.2 }}
                        >
                          ×
                        </Button>
                      </Stack>
                    </Stack>

                    <Grid container spacing={2}>
                      {/* <Grid item xs={12} md={3}>
                        <TextField
                          fullWidth
                          label="Item ID"
                          size="small"
                          value={item.id}
                          onChange={(e) => updateFeatureItem(sectionIndex, itemIndex, 'id', e.target.value)}
                        />
                      </Grid> */}
                      <Grid item xs={12} md={10}>
                        <TextField
                          fullWidth
                          label="Item Text"
                          multiline
                          // rows={2}
                          // size="small"
                          value={item.text}
                          onChange={(e) => updateFeatureItem(sectionIndex, itemIndex, 'text', e.target.value)}
                          placeholder="Enter feature description"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          fullWidth
                          label="Text Color"
                          type="color"
                          // size="small"
                          value={item.textColor}
                          onChange={(e) => updateFeatureItem(sectionIndex, itemIndex, 'textColor', e.target.value)}
                          sx={{ '& input': { height: 20 } }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            </Paper>
          ))}
        </Box>

        {/* Why Choose Section */}
        <Box sx={{ mb: 5 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 3 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: 'primary.main', borderRadius: 1, mr: 1.5 }} />
            <Typography variant="h6">Why Choose Section</Typography>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Why Choose Title"
                value={formData.whyChooseTitle}
                onChange={(e) => updateFormData('whyChooseTitle', e.target.value)}
                placeholder="Enter why choose title"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Title Color"
                type="color"
                value={formData.whyChooseTitleColor}
                onChange={(e) => updateFormData('whyChooseTitleColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>

            <Grid item xs={12} md={10}>
              <TextField
                fullWidth
                label="Why Choose Description"
                multiline
                rows={4}
                value={formData.whyChooseDescription}
                onChange={(e) => updateFormData('whyChooseDescription', e.target.value)}
                placeholder="Enter why choose description"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Description Color"
                type="color"
                value={formData.whyChooseDescriptionColor}
                onChange={(e) => updateFormData('whyChooseDescriptionColor', e.target.value)}
                sx={{ '& input': { height: 20 } }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Action Buttons */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={2}
          sx={{ pt: 3, borderTop: '1px solid', borderColor: 'divider' }}
        >
          <LoadingButton
            loading={loading}
            variant="contained"
            color="primary"
            onClick={handleSave}
            startIcon={<Iconify icon="eva:save-fill" />}
            size="large"
          >
            Update
          </LoadingButton>
        </Stack>
      </CardContent>
    </Card>
  );
}
